import { ActivatedRoute, Router } from '@angular/router';
import { ButtonComponent } from '../../../../shared/ui-common/button/button.component';
import { ChartComponent } from 'src/app/shared/ui-common/chart/chart.component';
import { ChartConfiguration, ScriptableContext } from 'chart.js';
import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  signal
  } from '@angular/core';
import { customChartTooltip } from 'src/app/shared/util/tooltip.util';
import { environment } from 'src/environments/environment.uat';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
  } from '@angular/forms';
import { MarkdownPipe } from '../../../../shared/data/pipes/markdown.pipe';
import { MenuItem } from 'src/app/shared/data/models/menu-item.model';
import { NoteToResponseTeam, SurveyIntervention } from '../../data/survey.model';
import { OnDemandType } from 'src/app/domains/on-demand/data/on-demand.model';
import { PillComponent } from 'src/app/shared/ui-common/pill/pill.component';
import { PillProgressBarComponent } from 'src/app/shared/ui-common/pill-progress-bar/pill-progress-bar.component';
import { PillService } from 'src/app/shared/data/pill.service';
import { SharedService } from 'src/app/shared/data/shared.service';
import { Subscription } from 'rxjs';
import { SurveyService } from '../../data/survey.service';
import { InterventionService } from '../../data/intervention.service';
import { TranslatePipe } from 'src/app/shared/data/pipes/translate.pipe';
import { TranslationService } from 'src/app/shared/data/translation.service';
import { VideoBackgroundComponent } from 'src/app/shared/ui-common/video-background/video-background.component';
import { WellbeingColorCodes } from 'src/app/shared/data/enums/wellbeing-colors.enum';

enum ActionType {
  NoAction = 0,
  ReadResponseTeam = 1,
  VideoResponseTeam = 2,
  RequestCall = 3,
  AddNote = 4,
  NoteSend = 5,
  GoToNext = 6,
  ReadAnonymized = 7
}

enum ViewType {
  BigScreenView = 0,
  MediumScreenView = 1,
  SmallScreenView = 2
}

@Component({
  selector: 'app-survey-intervention',
  imports: [
    CommonModule,
    ButtonComponent,
    ChartComponent,
    MarkdownPipe,
    TranslatePipe,
    VideoBackgroundComponent,
    PillComponent,
    PillProgressBarComponent,
    FormsModule,
    ReactiveFormsModule
  ],
  templateUrl: './survey-intervention.component.html',
  styleUrl: './survey-intervention.component.scss',
})
export class SurveyInterventionComponent implements OnInit, OnDestroy {
  surveyIntervention!: SurveyIntervention | null;
  s3BaseUrl = environment.s3BaseUrl;
  chartTextColor = '#fff';
  configuration!: ChartConfiguration;
  chartDate!: string | null;
  callBooked = false;
  noteTransition = false;
  actionContainerTransition = false;
  disableButton = false;
  actionTypes = ActionType;
  actionSignal = signal<ActionType>(ActionType.NoAction);
  viewTypes = ViewType;
  screenTypeViewSignal = signal<ViewType>(ViewType.BigScreenView);
  currentMenuItemSignal = signal<MenuItem | null>(null);
  nextAvailableMenuItem = signal<MenuItem | null>(null);
  type: OnDemandType = 'mind';
  routeSubscription!: Subscription;
  showResponseTeamVideo = false;
  languageId = '1045';
  fadeOut = false;
  fadeIn = false;
  videoFormat = '.mp4';
  noteToResponseTeam: NoteToResponseTeam | null = null;
  noteForm = new FormGroup({
    noteControl: new FormControl('', [Validators.required, Validators.minLength(5)])
  });

  private translationService = inject(TranslationService);

  constructor(
    private router: Router,
    private pillService: PillService,
    private route: ActivatedRoute,
    private surveyService: SurveyService,
    private sharedService: SharedService,
    private interventionService: InterventionService
  ) {
    effect(() => {
      this.currentMenuItemSignal.set(this.pillService.getCurrentMenuItem());
      this.nextAvailableMenuItem.set(this.pillService.getNextAvailableMenuItem());

      if (this.currentMenuItemSignal()?.hasOnDemandCase) {
        this.goToStep(ActionType.RequestCall);
      }
    });
  }

  get buttonTitle(): string {
    return this.nextAvailableMenuItem() ? 'WebApp.v1.Shared.Buttons.Next' : 'WebApp.v1.Shared.Buttons.Menu';
  }

  get isBodyIntervention(): boolean {
    return !this.surveyIntervention?.scores;
  }

  get currentDate(): string {
    if (this.surveyIntervention?.bodyAnswers) {
      const lastBodyAnswer = this.surveyIntervention?.bodyAnswers[this.surveyIntervention?.bodyAnswers?.length - 1];

      if (!lastBodyAnswer) {
        return '';
      }

      return new Date(lastBodyAnswer.finishedOn).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }

    const lastScore = this.surveyIntervention?.scores?.[this.surveyIntervention?.scores?.length - 1];

    if (!lastScore) {
      return '';
    }

    return new Date(lastScore.createdOn).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  get show3Columns(): boolean {
    return this.screenTypeViewSignal() === ViewType.BigScreenView;
  }

  get showFirstPart(): boolean {
    return this.screenTypeViewSignal() !== ViewType.BigScreenView && this.actionSignal() === this.defaultState;
  }

  get showSecondPart(): boolean {
    return this.screenTypeViewSignal() !== ViewType.BigScreenView && this.actionSignal() !== this.defaultState;
  }

  get showNote(): boolean {
    return this.actionSignal() === ActionType.AddNote || this.actionSignal() === ActionType.NoteSend;
  }

  get buttonFontSize(): number {
    return this.screenTypeViewSignal() === ViewType.SmallScreenView ? 20 : 14;
  }

  get chartTextTitle(): string {
    if (this.isBodyIntervention) {
      return 'WebApp.v1.Survey.SurveyIntervetion.Body.Chart.Title';
    }

    if (this.chartTextColor === WellbeingColorCodes.DarkGreen) {
      return 'WebApp.v1.Survey.SurveyIntervetion.Mind.Chart.YouWereThriving';
    } else if (this.chartTextColor === WellbeingColorCodes.Green) {
      return 'WebApp.v1.Survey.SurveyIntervetion.Mind.Chart.YouWereOkay';
    } else if (this.chartTextColor === WellbeingColorCodes.LightGreen) {
      return 'WebApp.v1.Survey.SurveyIntervetion.Mind.Chart.YouWereDoingWell';
    } else if (this.chartTextColor === WellbeingColorCodes.Yellow) {
      return 'WebApp.v1.Survey.SurveyIntervetion.Mind.Chart.WereYouOkay';
    } else if (this.chartTextColor === WellbeingColorCodes.Red) {
      return 'WebApp.v1.Survey.SurveyIntervetion.Mind.Chart.ActionNeeded';
    }

    return 'WebApp.v1.Survey.SurveyIntervetion.Mind.Chart.Title';
  }

  get nextAvailableItem(): MenuItem | null {
    return this.nextAvailableMenuItem();
  }

  get hasNextAvailableItem(): boolean {
    return this.nextAvailableMenuItem() !== null;
  }

  get videoName(): string {
    return this.isBodyIntervention ? 'wawes_body' : 'river';
  }

  get videoBackground(): string {
    return this.isBodyIntervention
      ? ''
      : `linear-gradient(0deg, rgba(4, 34, 80, 0.22), rgba(4, 34, 80, 0.22)),
        linear-gradient(0deg, rgba(0, 148, 255, 0.2), rgba(0, 148, 255, 0.2)),
        linear-gradient(180deg, rgba(25, 61, 115, 0) 29.71%, rgba(25, 61, 115, 0.49) 82.56%),
        linear-gradient(180deg, rgba(25, 61, 115, 0.55) 0%, rgba(25, 61, 115, 0) 26.28%),
        linear-gradient(0deg, rgba(4, 86, 146, 0.73), rgba(4, 86, 146, 0.73))`;
  }

  get showAnonymous(): boolean {
    return window.innerWidth >= 500 && window.innerHeight > 800;
  }

  get showLogo(): boolean {
    return window.innerWidth >= 1000 && window.innerHeight > 900;
  }

  get nextSurveyTitle(): string {
    return this.nextAvailableMenuItem()?.title ?? this.translationService.translate('WebApp.v1.Shared.Buttons.Menu');
  }

  get actionTitle(): string {
    switch (this.actionSignal()) {
      case ActionType.NoAction:
        return 'WebApp.v1.Survey.SurveyIntervetion.ContactMe';
      case ActionType.RequestCall:
        return this.isBodyIntervention
          ? 'WebApp.v1.Survey.SurveyIntervetion.CallBookedBody'
          : 'WebApp.v1.Survey.SurveyIntervetion.CallBooked';
      case ActionType.GoToNext:
        return 'WebApp.v1.Survey.SurveyIntervetion.ToNextSurvey';
      case ActionType.AddNote:
        return 'WebApp.v1.Survey.SurveyIntervetion.AddNote';
      case ActionType.NoteSend:
        return 'WebApp.v1.Survey.SurveyIntervetion.NoteSent';
    }

    return '';
  }

  get callBookedDescription(): string {
    return this.type === 'mind'
      ? 'WebApp.v1.Survey.SurveyIntervetion.CallBookedDescription.Mind'
      : 'WebApp.v1.Survey.SurveyIntervetion.CallBookedDescription.Body';
  }

  get defaultState(): ActionType {
    if (!this.surveyIntervention) {
      return ActionType.NoAction;
    }

    if (this.surveyIntervention.showContactMeButton) {
      return ActionType.NoAction;
    } else if (!this.surveyIntervention.showContactMeButton && this.surveyIntervention.showAddNoteButton) {
      return ActionType.RequestCall;
    }

    return ActionType.GoToNext;
  }

  @HostListener('window:resize')
  onResize(): void {
    this.checkScreenWidth();
  }

  ngOnInit(): void {
    this.videoFormat = this.sharedService.getVideoFormat();
    this.checkScreenWidth();
    this.languageId = this.translationService.getLanguageId();
    this.routeSubscription = this.route.params.subscribe(params => {
      this.type = params['type'] as OnDemandType || 'mind';
      this.surveyIntervention = this.interventionService.getIntervention(this.type);

      if (!this.surveyIntervention) {
        this.router.navigate(['/available-surveys']);

        return;
      }

      this.goToStep(this.defaultState);
    });

    if (this.isBodyIntervention) {
      this.initializeBodyChart();
    } else {
      this.initializeChart();
    }
  }

  ngOnDestroy(): void {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
  }

  onContactMe(): void {
    this.disableButton = true;
    const contactMe = this.type === 'mind'
      ? this.surveyService.contactMe()
      : this.surveyService.contactMeBody();

    contactMe.then(() => {
      this.callBooked = true;
      this.goToStep(ActionType.RequestCall);
      this.disableButton = false;
    });
  }

  onSendNote(): void {
    this.disableButton = true;
    const noteValue = this.noteForm.get('noteControl')?.value ?? '';
    const noteMethod = this.noteToResponseTeam
      ? this.surveyService.updateNote(this.noteToResponseTeam.id, noteValue)
      : this.surveyService.saveNote(noteValue);

    noteMethod.then(response => {
      this.noteForm.reset();
      this.noteToResponseTeam = response;
      this.goToStep(ActionType.NoteSend);
      this.disableButton = false;
    });
  }

  onGoToStats(): void {
    this.router.navigate(['/stats']);
  }

  onGoToNextSurvey(): void {
    const menuItem = this.nextAvailableMenuItem();

    if (menuItem) {
      this.pillService.setSelectedMenuItemById(menuItem.id);
      this.router.navigate(['/survey']);

      return;
    }

    this.router.navigate(['/available-surveys']);
  }

  goToStep(actionType: ActionType): void {
    this.actionContainerTransition = true;

    setTimeout(() => {
      this.actionSignal.set(actionType);
      this.actionContainerTransition = false;
      this.noteTransition = false;
    }, 400);
  }

  openHowdyVideo(): void {
    this.showResponseTeamVideo = true;
    this.fadeIn = false;

    requestAnimationFrame(() => {
      setTimeout(() => {
        this.fadeIn = true;
      }, 10);
    });
  }

  closeHowdyVideo(): void {
    this.fadeOut = true;
    this.fadeIn = false;

    setTimeout(() => {
      this.showResponseTeamVideo = false;
      this.fadeOut = false;
    }, 400);
  }

  private checkScreenWidth(): void {
    if (window.innerWidth > 1250) {
      this.screenTypeViewSignal.set(ViewType.BigScreenView);
    } else if (window.innerWidth < 1250 && window.innerWidth > 800) {
      this.screenTypeViewSignal.set(ViewType.MediumScreenView);
    } else {
      this.screenTypeViewSignal.set(ViewType.SmallScreenView);
    }
  }

  private initializeBodyChart(): void {
    if (!this.surveyIntervention?.bodyAnswers) {
      return;
    }

    const labels = this.surveyIntervention?.bodyAnswers.map(answer => new Date(answer.finishedOn)
      .toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
    const data = this.surveyIntervention?.bodyAnswers.map(answer => answer.answers[0].intAnswer);

    this.configuration = {
      type: 'line',
      data: {
        labels,
        datasets: [{
          data,
          borderColor: (ctx: ScriptableContext<'line'>): CanvasGradient | undefined => {
            const chart = ctx.chart;
            const { ctx: canvasCtx, chartArea } = chart;

            if (!chartArea) {
              return;
            }

            const gradient = canvasCtx.createLinearGradient(0, 0, chartArea.right, 0);
            const step = 1/labels.length;

            data.forEach((value, index) => {
              const iterator = step * (index + 1);

              if (value >= 8) {
                gradient.addColorStop(iterator, WellbeingColorCodes.Red);
              } else if (value >= 5) {
                gradient.addColorStop(iterator, WellbeingColorCodes.Yellow);
              } else if (value >= 3) {
                gradient.addColorStop(iterator, WellbeingColorCodes.LightGreen);
              } else if (value >= 1) {
                gradient.addColorStop(iterator, WellbeingColorCodes.Green);
              } else {
                gradient.addColorStop(iterator, WellbeingColorCodes.DarkGreen);
              }
            });

            return gradient;
          },
          borderWidth: 3,
          pointRadius: 6,
          pointBackgroundColor: '#fff',
          pointBorderColor: '#fff',
          pointHoverBorderWidth: 3,
          pointHoverRadius: 6,
          pointHoverBackgroundColor: (ctx: ScriptableContext<'line'>): string | undefined => {
            const chart = ctx.chart;
            const { ctx: chartArea } = chart;

            if (!chartArea) {
              return;
            }

            const index = ctx.dataIndex;
            const value = data[index];

            return this.getBodyChartColor(value);
          },
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false,
            external: (ctx): void => {
              const context = ctx.tooltip.dataPoints;
              const index = context[0].dataIndex;
              const title = labels[index];
              const label = this.getTooltipLabelText(index);

              if (!context) {
                return;
              }

              ctx.tooltip.title = [title];
              ctx.tooltip.body = [{
                before: [],
                lines: [label],
                after: []
              }];

              customChartTooltip(ctx);
            }
          }
        },
        scales: {
          x: { display: false },
          y: { display: false }
        },
      }
    };
  }

  private getPainLevelTranslation(painLevel: number): string {
    const translationMap: Record<number, string> = {
      10: 'WebApp.v1.Survey.SurveyIntervetion.Body.Chart.Tooltip.SeverePain',
      9: 'WebApp.v1.Survey.SurveyIntervetion.Body.Chart.Tooltip.StrongToSeverePain',
      8: 'WebApp.v1.Survey.SurveyIntervetion.Body.Chart.Tooltip.StrongPain',
      7: 'WebApp.v1.Survey.SurveyIntervetion.Body.Chart.Tooltip.StrongPain',
      6: 'WebApp.v1.Survey.SurveyIntervetion.Body.Chart.Tooltip.ModerateToStrongPain',
      5: 'WebApp.v1.Survey.SurveyIntervetion.Body.Chart.Tooltip.ModeratePain',
      4: 'WebApp.v1.Survey.SurveyIntervetion.Body.Chart.Tooltip.ModeratePain',
      3: 'WebApp.v1.Survey.SurveyIntervetion.Body.Chart.Tooltip.MildToModeratePain',
      2: 'WebApp.v1.Survey.SurveyIntervetion.Body.Chart.Tooltip.MildPain',
      1: 'WebApp.v1.Survey.SurveyIntervetion.Body.Chart.Tooltip.MildPain',
      0: 'WebApp.v1.Survey.SurveyIntervetion.Body.Chart.Tooltip.NoPain'
    };

    return translationMap[painLevel] || 'WebApp.v1.Survey.SurveyIntervetion.Body.Chart.Tooltip.MildPain';
  }

  private getTooltipLabelText(index: number): string {
    const bodyAnswer = this.surveyIntervention?.bodyAnswers?.[index];

    if (!bodyAnswer) {
      return '';
    }

    let label = '';
    bodyAnswer.answers.forEach(answer => {
      const painLevel = answer.intAnswer;
      const translationKey = this.getPainLevelTranslation(painLevel);
      const translatedText = this.translationService.translate(translationKey);

      if (answer.key === 'NoPain') {
        label = `${translatedText} <br>`;
      } else {
        const bodyPart = this.translationService.translate(`WebApp.v1.SurveyBody.BodyPart.${answer.key}`);
        label += `${bodyPart}: ${translatedText} <br>`;
      }
    });

    return label.trim();
  }

  private initializeChart(): void {
    let data: number[] = [];
    let labels: string[] = ['', '', '', '', '', ''];

    if (this.surveyIntervention?.scores && this.surveyIntervention.scores.length > 0) {
      labels = this.surveyIntervention.scores
        .map(score => new Date(score.createdOn)
          .toLocaleDateString('en-US', { month: 'short', day: 'numeric' }
        ));
      data = this.surveyIntervention.scores.map(score => score.score);
    }

    this.configuration = {
      type: 'line',
      data: {
        labels,
        datasets: [{
          data,
          borderColor: (ctx: ScriptableContext<'line'>): CanvasGradient | undefined => {
            const chart = ctx.chart;
            const { ctx: canvasCtx, chartArea } = chart;

            if (!chartArea) {
              return;
            }

            const gradient = canvasCtx.createLinearGradient(0, 0, chartArea.right, 0);
            const step = 1/labels.length;

            data.forEach((value, index) => {
              const iterator = step * (index + 1);

              if (value >= 90) {
                gradient.addColorStop(iterator, WellbeingColorCodes.DarkGreen);
              } else if (value >= 50) {
                gradient.addColorStop(iterator, WellbeingColorCodes.Green);
              } else if (value >= 35) {
                gradient.addColorStop(iterator, WellbeingColorCodes.Yellow);
              } else {
                gradient.addColorStop(iterator, WellbeingColorCodes.Red);
              }
            });

            return gradient;
          },
          borderWidth: 3,
          pointRadius: 6,
          pointBackgroundColor: '#fff',
          pointBorderColor: '#fff',
          pointHoverBorderWidth: 3,
          pointHoverBackgroundColor: (ctx: ScriptableContext<'line'>): string | undefined => {
            const chart = ctx.chart;
            const { ctx: chartArea } = chart;

            if (!chartArea) {
              return;
            }

            const index = ctx.dataIndex;
            const value = data[index];

            return this.getChartColor(value);
          },
          pointHoverRadius: 8,
          fill: false,
          tension: 0
        }, {
          data: [50, 50, 50, 50, 50, 50],
          borderColor: 'rgba(255, 255, 255, 0.5)',
          borderWidth: 1,
          pointBorderWidth: 0,
          pointHoverBorderWidth: 0,
          pointRadius: 0,
          pointHoverRadius: 0,
          borderDash: [3, 3],
          fill: false,
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false }
        },
        scales: {
          x: { display: false },
          y: { display: false }
        },
        onHover: (_, chartElement): void => {
          if (chartElement.length) {
            const index = chartElement[0].index;
            this.chartDate = labels[index];
            this.chartTextColor = this.getChartColor(data[index]);
          } else {
            this.chartDate = null;
            this.chartTextColor = '#fff';
          }
        }
      }
    };
  }

  private getBodyChartColor(value: number): string {
    const redZone = { start: 8, end: 10 };
    const yellowZone = { start: 5, end: 8 };
    const greenZone = { start: 3, end: 5 };
    const lightGreenZone = { start: 1, end: 3 };

    if (value >= redZone?.start) {
      return WellbeingColorCodes.Red;
    } else if (value >= yellowZone?.start) {
      return WellbeingColorCodes.Yellow;
    } else if (value >= greenZone?.start) {
      return WellbeingColorCodes.Green;
    } else if (value >= lightGreenZone?.start) {
      return WellbeingColorCodes.DarkGreen;
    }

    return WellbeingColorCodes.DarkGreen;
  }

  private getChartColor(value: number): string {
    if (!this.surveyIntervention?.wellbeingIntervals) {
      return '#2CDAFF';
    }

    const darkGreenZone = this.surveyIntervention?.wellbeingIntervals[4];
    const greenZone = this.surveyIntervention?.wellbeingIntervals[3];
    const lightGreenZone = this.surveyIntervention?.wellbeingIntervals[2];
    const yellowZone = this.surveyIntervention?.wellbeingIntervals[1];
    const redZone = this.surveyIntervention?.wellbeingIntervals[0];

    if (!darkGreenZone || !greenZone || !lightGreenZone || !yellowZone || !redZone) {
      return '#2CDAFF';
    }

    if (value >= darkGreenZone?.start) {
      return WellbeingColorCodes.DarkGreen;
    } else if (value >= greenZone?.start) {
      return WellbeingColorCodes.Green;
    } else if (value >= lightGreenZone?.start) {
      return WellbeingColorCodes.LightGreen;
    } else if (value >= yellowZone?.start) {
      return WellbeingColorCodes.Yellow;
    }

    return WellbeingColorCodes.Red;
  }
}
